import * as React from 'react'
import { Alert, Descriptions, Divider, Form, Input, Tag } from 'antd'
import { useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { AppService } from '@halo/services/app.service'
import './test-page-analysis-drawer.scss'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { Button, Drawer } from '@pushly/aqe/lib/components'
import { PageAnalysisService } from '@halo/services/page-analysis.service'
import ICategoryStrategy from '@halo/interfaces/category-strategy'
import { Icon } from '@ant-design/compatible'
import { useForm } from 'antd/es/form/Form'
import { IPageCategory } from '@pushly/aqe/lib/interfaces'
import { IPageAnalysis } from '@pushly/aqe/lib/interfaces'
import { getFriendlyPathName } from '@pushly/aqe/lib/utils'
import { VALID_URL_REGEX } from '@pushly/aqe/lib/constants'

export interface ITestPageAnalysisDrawerProps {
    title: string
    domainId?: number
    isPartial?: boolean
    hydratedPageAnalysis?: Partial<IHydratedPageAnalysisConfig>
    categoryStrategies?: Array<Partial<ICategoryStrategy>>
    pageCategories: IPageCategory[]
    onClose: () => void
}

function TestPageAnalysisDrawer(props: ITestPageAnalysisDrawerProps) {
    const appService = Container.get(AppService)
    const pageAnalysisService: PageAnalysisService = Container.get(PageAnalysisService)
    const [form] = useForm()

    const [isRunning, setIsRunning] = useState<boolean>()

    const [pageAnalysis, setPageAnalysis] = useState<IPageAnalysis>()
    const [errMessage, setErrMessage] = useState<string>()

    async function runAnalysis() {
        setErrMessage(null)
        setIsRunning(true)

        const res = await pageAnalysisService.analyze(
            form.getFieldValue('page_url'),
            props.domainId,
            null,
            props.hydratedPageAnalysis,
            props.categoryStrategies,
        )
        if (res.error) {
            setErrMessage(res.error.toString())
            setPageAnalysis(null)
        } else {
            setErrMessage(null)
            setPageAnalysis(res.data)
        }

        setIsRunning(false)
    }

    function hasConfiguration() {
        return !!(props.domainId || (props.categoryStrategies || []).length > 0 || props.hydratedPageAnalysis)
    }

    function getDescriptionRows(pa: IPageAnalysis) {
        const descriptionRows = []
        const fields: Array<keyof IPageAnalysis> = [
            'canonical_url',
            'type',
            'title',
            'description',
            'image_url',
            'keywords',
            'published_date',
        ]

        for (const field of fields) {
            const fieldValue = pa[field]

            if (!props.isPartial || fieldValue) {
                descriptionRows.push({
                    label: field,
                    value: fieldValue,
                })
            }
        }

        if (pa.additional_fields instanceof Object) {
            for (let key in pa.additional_fields) {
                if (pa.additional_fields.hasOwnProperty(key)) {
                    descriptionRows.push({
                        label: `additional_fields.${key}`,
                        value: pa.additional_fields[key],
                    })
                }
            }
        }

        descriptionRows.push({
            label: 'page_categories',
            value: pa.page_categories,
        })

        return descriptionRows
    }

    function render() {
        return (
            <Drawer
                width="60%"
                getContainer={appService.getAppContainer}
                className="edit-category-drawer"
                title={props.title}
                placement="right"
                onClose={props.onClose}
                hideFooter={true}
                visible={hasConfiguration()}
            >
                {hasConfiguration() && (
                    <Form
                        layout="horizontal"
                        form={form}
                        onFinish={() => {
                            runAnalysis()
                        }}
                        onSubmitCapture={() => {
                            form.submit()
                        }}
                    >
                        <Form.Item
                            label="Page Url"
                            name="page_url"
                            rules={[
                                { required: true, message: 'Page Url is required' },
                                { pattern: VALID_URL_REGEX, message: 'Must be a valid url' },
                            ]}
                        >
                            <div className="test-page-url-input">
                                <Input size="small" autoComplete="off" disabled={isRunning} />
                                <Button
                                    disabled={isRunning}
                                    onClick={() => {
                                        form.submit()
                                    }}
                                >
                                    {isRunning ? (
                                        <Icon type="loading-3-quarters" spin={true} />
                                    ) : (
                                        <Icon type="caret-right" />
                                    )}
                                </Button>
                            </div>
                        </Form.Item>
                    </Form>
                )}

                {(errMessage || pageAnalysis) && <Divider />}

                {errMessage && <Alert message="Fatal Execution Error" description={errMessage} type="error" />}

                {pageAnalysis && (
                    <>
                        <Descriptions layout="horizontal" title="Page Analysis Results" column={1} bordered={true}>
                            {getDescriptionRows(pageAnalysis).map((dr) => {
                                return (
                                    <Descriptions.Item key={dr.label} label={dr.label}>
                                        {dr.label === 'keywords' &&
                                            ((dr.value as string[]) || []).map((k, idx) => <Tag key={idx}>{k}</Tag>)}

                                        {dr.label === 'page_categories' &&
                                            ((dr.value as number[]) || []).map((pcId, idx) => (
                                                <Tag key={idx}>
                                                    {getFriendlyPathName(
                                                        props.pageCategories,
                                                        props.pageCategories.find((pc) => pc.id === pcId),
                                                    )}
                                                </Tag>
                                            ))}

                                        {!['keywords', 'page_categories'].includes(dr.label) && dr.value}
                                    </Descriptions.Item>
                                )
                            })}
                        </Descriptions>
                    </>
                )}
            </Drawer>
        )
    }

    return render()
}

export default TestPageAnalysisDrawer
